/* eslint-disable @typescript-eslint/no-explicit-any */
import { Editor } from '@tiptap/react';
import {
  Dispatch,
  FunctionComponent,
  memo,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { isMobileOnly } from 'react-device-detect';
import { useTranslation } from 'react-i18next';
import { DetailsBlock } from '../../../../components';
import { User } from '../../../../services';
import { FileAcceptType, useTags } from '../../../hooks';
import { GalleryItem } from '../../../models';
import { getCssVar, isEmptyString, stripHtmlString } from '../../../utils';
import { Button, ButtonType } from '../../Button';
import { FilePreview, FileUpload } from '../../File';
import { IconLabel } from '../../IconLabel';
import { Modal } from '../../Modal';
import { TextAreaField } from '../../TextAreaField';
import {
  blocksToContentConverter,
  contentToBlocksConverter,
  EditorStats,
  FULL_POST_EDITOR_DEFAULT_CONTENT,
  TextEditor,
  TextEditorVariant,
} from '../../TextEditor';
import { UserMentionPreview } from '../../UserMentionPreview';
import { CreateCardImport, CreateCardSettingsTabType, FullPostFields } from '../CreateCardModal';
import { PostToggleFields, PostToggleFieldsItem } from '../PostToggleFields';

import classNames from 'classnames';
import classes from './FullPost.module.scss';

interface FullPostProps {
  loading: boolean;
  setLoading: Dispatch<SetStateAction<boolean>>;
  fullPostFields: FullPostFields;
  setFullPostFields: Dispatch<SetStateAction<FullPostFields>>;
  setFullPostGalleryItems: (galleryItems: GalleryItem[]) => void;
  fullPostTextEditorOpen: boolean;
  setFullPostTextEditorOpen: Dispatch<SetStateAction<boolean>>;
  fullPostTextEditorUsePrefill: boolean;
  setFullPostTextEditorUsePrefill: Dispatch<SetStateAction<boolean>>;
  setSettingsOpen: (isOpen: boolean) => void;
  canOpenSettings: boolean;
  setActiveFullPostSettingsTabIndex: (index: number) => void;
  mentionedUsers: User[];
  setUserMentionModalOpen: Dispatch<SetStateAction<boolean>>;
  postInStoryAllowMentions: boolean;
  locale?: string;
}

type FullPostToggleFields = {
  [key in keyof FullPostFields]?: PostToggleFieldsItem;
};

export const FullPost: FunctionComponent<FullPostProps> = memo(
  ({
    loading,
    setLoading,
    fullPostFields,
    setFullPostFields,
    setFullPostGalleryItems,
    fullPostTextEditorOpen,
    setFullPostTextEditorOpen,
    fullPostTextEditorUsePrefill,
    setFullPostTextEditorUsePrefill,
    setSettingsOpen,
    canOpenSettings,
    setActiveFullPostSettingsTabIndex,
    mentionedUsers,
    setUserMentionModalOpen,
    postInStoryAllowMentions,
    locale,
  }) => {
    const { t } = useTranslation();

    const { t: ctxT } = useTranslation('', { keyPrefix: 'addContent' });

    const { hasContentTags } = useTags();

    const sourceNameRef = useRef<HTMLTextAreaElement>(null);

    const titleRef = useRef<HTMLTextAreaElement>(null);

    const abstractRef = useRef<HTMLTextAreaElement>(null);

    const authorRef = useRef<HTMLTextAreaElement>(null);

    const editorRef = useRef<Editor | null>(null);

    const { sourceName, headline, title, abstract, contentAuthor, gallery, contentBlocks } =
      fullPostFields;

    const editorContent = useMemo(() => {
      if (!contentBlocks.length) {
        return FULL_POST_EDITOR_DEFAULT_CONTENT;
      }

      return blocksToContentConverter(contentBlocks);
    }, [contentBlocks]);

    const [postFieldsToggling, setPostFieldsToggling] = useState<FullPostToggleFields>({
      sourceName: {
        ref: sourceNameRef,
        title: ctxT('full-post-sourceName'),
        iconId: '',
        hidden: Boolean(isEmptyString(sourceName)),
      },
      abstract: {
        ref: abstractRef,
        title: ctxT('full-post-abstract'),
        iconId: '',
        hidden: false,
      },
      contentAuthor: {
        ref: authorRef,
        title: ctxT('full-post-author'),
        iconId: '',
        hidden: Boolean(isEmptyString(contentAuthor)),
      },
    });

    const [contentBlocksDraft, setContentBlocksDraft] = useState(contentBlocks);

    const [previewMode, setPreviewMode] = useState(false);

    const [editor, setEditor] = useState<Editor | null>(null);

    useEffect(() => {
      const title = titleRef.current;

      if (!title || fullPostTextEditorOpen) {
        return;
      }

      title.setSelectionRange(title.value.length, title.value.length);

      setTimeout(() => title.focus(), 0);
    }, [fullPostTextEditorOpen]);

    const fieldChangeHandler = useCallback(
      (name: keyof FullPostFields, value: string) =>
        setFullPostFields({ ...fullPostFields, [name]: value }),
      [fullPostFields, setFullPostFields]
    );

    const keyDownHandler = useCallback(
      (event: React.KeyboardEvent<HTMLTextAreaElement>, field: keyof FullPostFields) => {
        const { key, target } = event;

        if (key === 'Backspace' && !(target as HTMLTextAreaElement).value) {
          setPostFieldsToggling({
            ...postFieldsToggling,
            [field]: { ...postFieldsToggling[field], hidden: true },
          });
        }
      },
      [postFieldsToggling]
    );

    const onTextEditorSave = useCallback(async () => {
      setFullPostTextEditorOpen(false);

      if (!editorRef.current) {
        return;
      }

      const contentBlocks = contentToBlocksConverter(editorRef.current.getJSON().content ?? []);

      const header =
        (
          fullPostTextEditorUsePrefill &&
          (contentBlocks.find(({ type }) => type === 'header')?.data as any)
        )?.text ?? '';

      const paragraph =
        (
          fullPostTextEditorUsePrefill &&
          (contentBlocks.find(({ type }) => type === 'paragraph')?.data as any)
        )?.text ?? '';

      const image = (
        fullPostTextEditorUsePrefill &&
        (contentBlocks.find(({ type }) => type === 'image')?.data as any)
      )?.items?.[0]?.image;

      const abstractValue = abstract || stripHtmlString(paragraph);

      setFullPostFields({
        ...fullPostFields,
        title: title || stripHtmlString(header).replace(/(\r\n|\n|\r)/gm, ' '),
        abstract: abstractValue,
        gallery: !gallery.length && image ? [{ image }] : gallery,
        contentBlocks,
      });

      setFullPostTextEditorUsePrefill(false);
    }, [
      abstract,
      fullPostFields,
      fullPostTextEditorUsePrefill,
      gallery,
      setFullPostFields,
      setFullPostTextEditorOpen,
      setFullPostTextEditorUsePrefill,
      title,
    ]);

    const previewBtnLabel = useMemo(
      () => (previewMode ? ctxT('full-post-open-text-editor') : t('common.preview')),
      [ctxT, previewMode, t]
    );

    const previewHandler = useCallback(() => {
      if (!previewMode && editorRef.current) {
        setContentBlocksDraft(contentToBlocksConverter(editorRef.current.getJSON().content ?? []));
      }
      setPreviewMode(!previewMode);
    }, [previewMode]);

    const textEditorBody = useMemo(() => {
      return (
        <div className={classes['full-post__editor']}>
          <div
            className={classNames(classes['full-post__editor-header'], {
              [classes['full-post__editor-header--mobile']]: isMobileOnly,
            })}
          >
            {!previewMode && (
              <>
                <IconLabel
                  iconId={'search'}
                  iconSize={18}
                  label={t('common.search')}
                  color={'#b3b3b3'}
                  hoverColor={'#fff'}
                  className={classes['full-post__editor-header-search']}
                  onMouseDown={(event) => event.preventDefault()}
                  onClick={() => editor?.emit('openFindReplace')}
                />
                <CreateCardImport
                  editorRef={editorRef}
                  setFullPostTextEditorUsePrefill={setFullPostTextEditorUsePrefill}
                />
              </>
            )}
            <Button
              type={ButtonType.primary}
              label={previewBtnLabel}
              className={classes['full-post__editor-header-preview']}
              onClick={previewHandler}
            />
            {!previewMode && (
              <span
                className={classes['full-post__editor-header-close']}
                onClick={() => setFullPostTextEditorOpen(false)}
              ></span>
            )}
          </div>

          <div
            className={classNames(classes['full-post__editor-content'], {
              [classes['full-post__editor-content--hidden']]: previewMode,
              [classes['full-post__editor-content--mobile']]: isMobileOnly,
            })}
          >
            <TextEditor
              variant={TextEditorVariant.FULL_POST}
              editorRef={editorRef}
              content={editorContent}
              onEditorChange={setEditor}
              locale={locale}
            />
          </div>

          {previewMode && (
            <div
              className={classNames(
                classes['full-post__editor-content'],
                classes[`full-post__editor-content--${isMobileOnly ? 'preview-mobile' : 'preview'}`]
              )}
            >
              {contentBlocksDraft.map((block, index) => (
                <DetailsBlock key={`${block.type}${index}`} block={block} />
              ))}
            </div>
          )}

          {!previewMode && (
            <div
              className={classNames(classes['full-post__editor-footer'], {
                [classes['full-post__editor-footer--mobile']]: isMobileOnly,
              })}
            >
              {editor && <EditorStats editor={editor} />}
              <Button
                type={ButtonType.secondary}
                label={t('common.cancel')}
                onClick={() => setFullPostTextEditorOpen(false)}
                className={classes['full-post__editor-footer-cancel']}
              />
              <Button
                type={ButtonType.primary}
                label={t('common.save')}
                onClick={onTextEditorSave}
              />
            </div>
          )}
        </div>
      );
    }, [
      contentBlocksDraft,
      editor,
      editorContent,
      locale,
      onTextEditorSave,
      previewBtnLabel,
      previewHandler,
      previewMode,
      setFullPostTextEditorOpen,
      setFullPostTextEditorUsePrefill,
      t,
    ]);

    const tagsClickHandler = useCallback(() => {
      setActiveFullPostSettingsTabIndex(CreateCardSettingsTabType.TAGS);
      setSettingsOpen(true);
    }, [setActiveFullPostSettingsTabIndex, setSettingsOpen]);

    const content = useMemo(() => {
      return (
        <>
          <TextAreaField
            inputClassName={classes['full-post__headline']}
            value={headline}
            placeholder={ctxT('full-post-headline')}
            maxLength={360}
            disabled={loading}
            onChange={({ target }) => fieldChangeHandler('headline', target.value)}
          />

          <UserMentionPreview
            mentionedUsers={mentionedUsers}
            {...(postInStoryAllowMentions && { onClick: () => setUserMentionModalOpen(true) })}
          />

          <FilePreview
            galleryItems={gallery}
            setGalleryItems={setFullPostGalleryItems}
            hasSettings
            hideCaption
          />

          <FileUpload
            galleryItems={gallery}
            setGalleryItems={setFullPostGalleryItems}
            loading={loading}
            setLoading={setLoading}
            fileAcceptTypes={[FileAcceptType.IMAGE]}
            uploadLimit={1}
          />

          <div className={classes['full-post__fields']}>
            <TextAreaField
              ref={sourceNameRef}
              inputClassName={classes['full-post__sourceName']}
              value={sourceName}
              placeholder={ctxT('full-post-sourceName')}
              maxLength={160}
              disabled={loading}
              onChange={({ target }) => fieldChangeHandler('sourceName', target.value)}
              onKeyDown={(event) => keyDownHandler(event, 'sourceName')}
              hidden={postFieldsToggling?.sourceName?.hidden}
              disableLineBreak
            />

            <TextAreaField
              ref={titleRef}
              inputClassName={classes['full-post__title']}
              value={title}
              placeholder={ctxT('full-post-title')}
              maxLength={160}
              disabled={loading}
              onChange={({ target }) => fieldChangeHandler('title', target.value)}
              disableLineBreak
            />

            <TextAreaField
              ref={abstractRef}
              inputClassName={classes['full-post__abstract']}
              value={abstract}
              placeholder={ctxT('full-post-abstract')}
              maxLength={700}
              disabled={loading}
              onChange={({ target }) => fieldChangeHandler('abstract', target.value)}
              onKeyDown={(event) => keyDownHandler(event, 'abstract')}
              hidden={postFieldsToggling?.abstract?.hidden}
            />

            <TextAreaField
              ref={authorRef}
              inputClassName={classes['full-post__author']}
              value={contentAuthor}
              placeholder={ctxT('full-post-author')}
              maxLength={60}
              disabled={loading}
              onChange={({ target }) => fieldChangeHandler('contentAuthor', target.value)}
              onKeyDown={(event) => keyDownHandler(event, 'contentAuthor')}
              hidden={postFieldsToggling?.contentAuthor?.hidden}
              disableLineBreak
            />
          </div>
        </>
      );
    }, [
      abstract,
      contentAuthor,
      ctxT,
      fieldChangeHandler,
      gallery,
      headline,
      keyDownHandler,
      loading,
      mentionedUsers,
      postFieldsToggling?.abstract?.hidden,
      postFieldsToggling?.contentAuthor?.hidden,
      postFieldsToggling?.sourceName?.hidden,
      postInStoryAllowMentions,
      setFullPostGalleryItems,
      setLoading,
      setUserMentionModalOpen,
      sourceName,
      title,
    ]);

    return (
      <>
        <div
          className={classNames(classes['full-post'], {
            [classes['full-post--mobile']]: isMobileOnly,
          })}
        >
          <div
            className={classNames(classes['full-post__scrollable'], {
              [classes['full-post__scrollable--mobile']]: isMobileOnly,
            })}
          >
            {content}
          </div>
          <div className={classes['full-post__pane']}>
            <div className={classes['full-post__pane-fields']}>
              {postInStoryAllowMentions && (
                <IconLabel
                  iconId={'mention'}
                  iconSize={20}
                  label={ctxT('mention')}
                  color={getCssVar('--create-card-link-color')}
                  hoverColor={getCssVar('--create-card-link-hover-color')}
                  onClick={() => setUserMentionModalOpen(true)}
                />
              )}
              {canOpenSettings && hasContentTags && (
                <IconLabel
                  iconId={'tags'}
                  iconSize={20}
                  label={ctxT('tags')}
                  color={getCssVar('--create-card-link-color')}
                  hoverColor={getCssVar('--create-card-link-hover-color')}
                  onClick={tagsClickHandler}
                />
              )}
              <PostToggleFields
                loading={loading}
                fields={postFieldsToggling}
                setFields={setPostFieldsToggling}
              />
            </div>
          </div>
        </div>

        <Modal
          isOpen={fullPostTextEditorOpen}
          body={textEditorBody}
          contentStyle={{
            padding: '0',
            top: '0',
            width: '100%',
            maxWidth: '100%',
            height: '100dvh',
            maxHeight: '100dvh',
            overflowX: 'hidden',
          }}
          alignTop
          onClose={() => setFullPostTextEditorOpen(false)}
          root={'modal-editor-root'}
          ariaLabel={ctxT('full-post')}
        />
      </>
    );
  }
);
